import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import "./StockLtpAnalysis.css";
import { environment } from "environment/environment";
import usePageTitle from "component/common/PageTitle";
import useStockApiWebsocket from "service/websocket/StockApiWebsocket";

const STOCK_PREVIOUS_LTP_DATA_URL = `${environment.stockApiBaseUrl}/stock-option-ltp-analysis/stock-previous-data`;

const RANGE_OPTIONS = [
  { label: "ALL", value: "ALL" },
  { label: "ATM", value: "10" },
];

const OPTION_ROW_LIST_KEYS = [
  "optionDataModelList",
  "stockOptionDataModelList",
  "stockOptionDataList",
  "optionChainDataList",
  "optionChainList",
];

const STOCK_EXPIRY_DATE_LIST_KEY = "STOCK_EXPIRY_DATE_LIST";

const isStockExpiryDateListKey = (key) =>
  String(key || "").trim().toUpperCase() === STOCK_EXPIRY_DATE_LIST_KEY;

const parseMaybeNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const toNumber = (value) => parseMaybeNumber(value) ?? 0;

const pickFirstFiniteNumber = (...values) => {
  for (const value of values) {
    const parsed = parseMaybeNumber(value);
    if (parsed != null) {
      return parsed;
    }
  }

  return null;
};

const isObject = (value) => value !== null && typeof value === "object" && !Array.isArray(value);

const getDirectSymbol = (value, fallbackValue = "STOCK") =>
  value?.stockLiveSymbol ??
  value?.symbol ??
  value?.stockSymbol ??
  value?.indexSymbol ??
  value?.underlyingSymbol ??
  value?.ceShortName ??
  value?.peShortName ??
  value?.ceOriginalName ??
  value?.peOriginalName ??
  value?.name ??
  fallbackValue;

const hasOptionRows = (value) =>
  isObject(value) && OPTION_ROW_LIST_KEYS.some((key) => Array.isArray(value[key]));

const hasStockOptionRowFields = (value) =>
  isObject(value) &&
  (value.stockStrikePrice != null ||
    value.stockSpotPrice != null ||
    value.stockLiveSymbol != null ||
    value.ceSymbol != null ||
    value.peSymbol != null);

const normalizeMapCandidate = (candidate) => {
  if (!candidate) {
    return {};
  }

  if (candidate instanceof Map) {
    return Object.fromEntries(
      [...candidate.entries()].filter(([key]) => !isStockExpiryDateListKey(key))
    );
  }

  if (Array.isArray(candidate)) {
    return candidate.reduce((accumulator, item, index) => {
      if (Array.isArray(item) && item.length >= 2) {
        accumulator[item[0]] = item[1];
        return accumulator;
      }

      if (hasStockOptionRowFields(item)) {
        const symbol = getDirectSymbol(item, `STOCK-${index + 1}`);
        accumulator[symbol] = Array.isArray(accumulator[symbol])
          ? [...accumulator[symbol], item]
          : [item];
        return accumulator;
      }

      const symbol = getDirectSymbol(item, item?.key ?? `STOCK-${index + 1}`);
      const value = hasOptionRows(item?.value) ? item.value : item;
      accumulator[symbol] = value;
      return accumulator;
    }, {});
  }

  if (isObject(candidate)) {
    return Object.fromEntries(
      Object.entries(candidate).filter(([key]) => !isStockExpiryDateListKey(key))
    );
  }

  return {};
};

const normalizeStockOptionDataMap = (payload) => {
  if (!payload) {
    return {};
  }

  const wrappedMap =
    payload?.stockOptionDataMap ??
    payload?.data?.stockOptionDataMap ??
    payload?.payload?.stockOptionDataMap;

  if (wrappedMap) {
    return normalizeMapCandidate(wrappedMap);
  }

  if (hasOptionRows(payload)) {
    return {
      [getDirectSymbol(payload)]: payload,
    };
  }

  if (hasStockOptionRowFields(payload)) {
    return {
      [getDirectSymbol(payload)]: [payload],
    };
  }

  if (Array.isArray(payload)) {
    return normalizeMapCandidate(payload);
  }

  if (isObject(payload)) {
    return Object.entries(payload).reduce((accumulator, [key, value]) => {
      if (
        value == null ||
        key === "stockOptionDataMap" ||
        isStockExpiryDateListKey(key)
      ) {
        return accumulator;
      }

      if (Array.isArray(value) || isObject(value)) {
        accumulator[key] = value;
      }

      return accumulator;
    }, {});
  }

  return {};
};

const resolveStockExpiryDateList = (payload) => {
  const payloadCandidates = [payload, payload?.data, payload?.payload];

  for (const candidate of payloadCandidates) {
    if (!isObject(candidate)) continue;

    const expiryDateEntry = Object.entries(candidate).find(([key]) =>
      isStockExpiryDateListKey(key)
    );

    if (Array.isArray(expiryDateEntry?.[1])) {
      return expiryDateEntry[1].filter(Boolean);
    }
  }

  return [];
};

const buildStockExpirySymbol = (expiryDate) => {
  const match = String(expiryDate || "").match(/^(\d{2})-(\d{2})-(\d{4})$/);

  if (!match) return "";

  const [, , month, year] = match;
  const monthName = [
    "JAN", "FEB", "MAR", "APR", "MAY", "JUN",
    "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"
  ][Number(month) - 1];

  return monthName ? `${year.slice(-2)}${monthName}` : "";
};

const normalizePreviousLtpRows = (payload) => {
  const rows = Array.isArray(payload?.data)
    ? payload.data
    : Array.isArray(payload)
      ? payload
      : [];

  return rows.filter((row) => getStrikePrice(row) != null);
};

const resolveOptionRows = (marketData) => {
  if (Array.isArray(marketData)) {
    return marketData;
  }

  if (hasStockOptionRowFields(marketData)) {
    return [marketData];
  }

  if (!isObject(marketData)) {
    return [];
  }

  for (const key of OPTION_ROW_LIST_KEYS) {
    if (Array.isArray(marketData[key])) {
      return marketData[key];
    }
  }

  return [];
};

const resolveDayRangeData = (marketData) =>
  marketData?.stockDayRange ??
  marketData?.stockDayRangeData ??
  marketData?.stockDayRangeModel ??
  marketData?.indexDayRange ??
  marketData?.dayRange ??
  marketData?.dayRangeData ??
  {};

const resolveLiveData = (marketData) =>
  marketData?.stockLiveDataModel ??
  marketData?.indexLiveDataModel ??
  marketData?.liveDataModel ??
  marketData?.liveData ??
  {};

const resolveSymbolFromMarket = (marketData, fallbackSymbol) => {
  const rows = resolveOptionRows(marketData);
  return getDirectSymbol(marketData, getDirectSymbol(rows[0], fallbackSymbol));
};

const sortByStrike = (rows = []) =>
  [...rows].sort((firstRow, secondRow) => {
    return getStrikePrice(firstRow) - getStrikePrice(secondRow);
  });

const filterOptionRows = (rows, spotPrice, rangeKey) => {
  if (!Array.isArray(rows) || rows.length === 0) {
    return [];
  }

  const sideCount = Number(rangeKey);
  const validSpotPrice = parseMaybeNumber(spotPrice);

  if (!Number.isFinite(sideCount) || sideCount <= 0 || validSpotPrice == null) {
    return rows;
  }

  const closestIndex = rows.reduce((closestRowIndex, row, rowIndex) => {
    const strikePrice = getStrikePrice(row);
    const closestStrikePrice = getStrikePrice(rows[closestRowIndex]);
    const currentDifference = Math.abs(toNumber(strikePrice) - validSpotPrice);
    const closestDifference = Math.abs(toNumber(closestStrikePrice) - validSpotPrice);

    return currentDifference < closestDifference ? rowIndex : closestRowIndex;
  }, 0);

  const startIndex = Math.max(0, closestIndex - sideCount);
  const endIndex = Math.min(rows.length - 1, closestIndex + sideCount);

  return rows.slice(startIndex+1, endIndex+1);
};

const isPointDifferenceWithinLimit = (value1, value2, limit = 10) => {
  const firstValue = parseMaybeNumber(value1);
  const secondValue = parseMaybeNumber(value2);

  if (firstValue == null || secondValue == null) {
    return false;
  }

  return Math.abs(firstValue - secondValue) <= limit;
};

const trimTrailingZeroes = (value) =>
  value.replace(/\.0+$/, "").replace(/(\.\d*[1-9])0+$/, "$1");

const formatNumber = (value, digits = 0) => {
  const parsed = parseMaybeNumber(value);

  if (parsed == null) {
    return "--";
  }

  return new Intl.NumberFormat("en-IN", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(parsed);
};

const formatPrice = (value, digits = 1) => {
  const parsed = parseMaybeNumber(value);

  if (parsed == null) {
    return "--";
  }

  return new Intl.NumberFormat("en-IN", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(parsed);
};

const formatPercentValue = (value, digits = 2) => {
  const parsed = parseMaybeNumber(value);

  if (parsed == null) {
    return "--";
  }

  return `${trimTrailingZeroes(parsed.toFixed(digits))}%`;
};

const formatStockSymbolName = (value) => {
  const rawValue = typeof value === "string" ? value.trim() : "";

  if (!rawValue) {
    return "STOCK";
  }

  const symbolWithoutExchange = rawValue.includes(":") ? rawValue.split(":").pop() || rawValue : rawValue;
  return symbolWithoutExchange.replace(/-(EQ|STK|NSE)$/i, "");
};

const getLtpRangeTierClass = (value) => {
  const parsed = parseMaybeNumber(value);

  if (parsed == null) {
    return "";
  }

  if (parsed >= 90) {
    return "stock-ltp-range-tier-blue";
  }

  if (parsed >= 80) {
    return "stock-ltp-range-tier-yellow";
  }

  if (parsed >= 70) {
    return "stock-ltp-range-tier-pink";
  }

  return "";
};

const getMaxHighLtpPercentage = (ltpValue, highValue) => {
  const ltp = parseMaybeNumber(ltpValue);
  const high = parseMaybeNumber(highValue);

  if (ltp == null || high == null || high === 0) {
    return null;
  }

  return ((high - ltp) / high) * 100;
};

const getStrikePrice = (row) =>
  pickFirstFiniteNumber(
    row?.stockStrikePrice,
    row?.strikePrice,
    row?.strike,
    row?.optionStrikePrice
  );

const buildLtpAnalysisRow = (row) => {
  const strikePrice = getStrikePrice(row);

  if (strikePrice == null) {
    return null;
  }

  const callMaxLow = pickFirstFiniteNumber(
    row.ceMaxLow,
    row.callMaxLow,
    row.ceLowPrice,
    row.ceLow,
    row.callLow
  );
  const callMaxHigh = pickFirstFiniteNumber(
    row.ceMaxHigh,
    row.callMaxHigh,
    row.ceHighPrice,
    row.ceHigh,
    row.callHigh
  );
  const callLtp = pickFirstFiniteNumber(row.ceLtp, row.callLtp, row.ceLastPrice, row.callLastPrice);
  const putMaxLow = pickFirstFiniteNumber(
    row.peMaxLow,
    row.putMaxLow,
    row.peLowPrice,
    row.peLow,
    row.putLow
  );
  const putMaxHigh = pickFirstFiniteNumber(
    row.peMaxHigh,
    row.putMaxHigh,
    row.peHighPrice,
    row.peHigh,
    row.putHigh
  );
  const putLtp = pickFirstFiniteNumber(row.peLtp, row.putLtp, row.peLastPrice, row.putLastPrice);

  return {
    strikePrice,
    callMaxLow,
    callMaxHigh,
    callLtp,
    callHighLowPercentage: pickFirstFiniteNumber(
      row.ceMaxHighLowPercentage,
      row.callMaxHighLowPercentage
    ),
    callDownPercentage: pickFirstFiniteNumber(
      row.ceMaxHighLtpPercentage,
      row.ceRangePercentage,
      row.callRangePercentage,
      getMaxHighLtpPercentage(callLtp, callMaxHigh)
    ),
    putLtp,
    putDownPercentage: pickFirstFiniteNumber(
      row.peMaxHighLtpPercentage,
      row.peRangePercentage,
      row.putRangePercentage,
      getMaxHighLtpPercentage(putLtp, putMaxHigh)
    ),
    putHighLowPercentage: pickFirstFiniteNumber(
      row.peMaxHighLowPercentage,
      row.putMaxHighLowPercentage
    ),
    putMaxHigh,
    putMaxLow,
  };
};

export default function StockLtpAnalysis() {
  const { stockOptionData } = useStockApiWebsocket(
    environment.stockApiDayRangeDataWSUrl,
    ["stockOptionData"]
  );
  const [selectedStrikeFilter, setSelectedStrikeFilter] = useState("10");
  const [selectedExpiryDate, setSelectedExpiryDate] = useState("");
  const [selectedSymbol, setSelectedSymbol] = useState("");
  const [previousExpiryRows, setPreviousExpiryRows] = useState([]);
  const [isPreviousExpiryLoading, setIsPreviousExpiryLoading] = useState(false);
  const [previousExpiryError, setPreviousExpiryError] = useState("");

  const stockOptionDataMap = useMemo(
    () => normalizeStockOptionDataMap(stockOptionData),
    console.log("stockOptionDataMap", stockOptionData),
    [stockOptionData]
  );
  const stockExpiryDateList = useMemo(
    () => resolveStockExpiryDateList(stockOptionData),
    [stockOptionData]
  );

  const symbolOptions = useMemo(
    () =>
      Object.entries(stockOptionDataMap)
        .map(([symbol, marketData]) => ({
          value: symbol,
          label: formatStockSymbolName(resolveSymbolFromMarket(marketData, symbol)),
        }))
        .sort((firstOption, secondOption) => firstOption.label.localeCompare(secondOption.label)),
    [stockOptionDataMap]
  );

  useEffect(() => {
    if (symbolOptions.length === 0) {
      setSelectedSymbol("");
      return;
    }

    if (!selectedSymbol || !stockOptionDataMap[selectedSymbol]) {
      setSelectedSymbol(symbolOptions[0].value);
    }
  }, [selectedSymbol, stockOptionDataMap, symbolOptions]);

  const selectedMarketData = selectedSymbol ? stockOptionDataMap[selectedSymbol] : null;
  const liveOptionRows = useMemo(
    () => sortByStrike(resolveOptionRows(selectedMarketData)),
    [selectedMarketData]
  );
  const liveFirstOptionRow = liveOptionRows?.[0] ?? null;
  const currentExpiryDate = liveFirstOptionRow?.stockExpiryDate ?? "";

  useEffect(() => {
    setSelectedExpiryDate((previousExpiryDate) => {
      if (stockExpiryDateList.includes(previousExpiryDate)) {
        return previousExpiryDate;
      }

      return stockExpiryDateList.includes(currentExpiryDate)
        ? currentExpiryDate
        : stockExpiryDateList[0] || "";
    });
  }, [currentExpiryDate, stockExpiryDateList]);

  useEffect(() => {
    const isCurrentExpiry =
      !selectedExpiryDate || selectedExpiryDate === currentExpiryDate;

    if (!selectedSymbol || isCurrentExpiry) {
      setPreviousExpiryRows([]);
      setPreviousExpiryError("");
      setIsPreviousExpiryLoading(false);
      return undefined;
    }

    const expirySymbol = buildStockExpirySymbol(selectedExpiryDate);

    if (!expirySymbol) {
      setPreviousExpiryRows([]);
      setPreviousExpiryError("Invalid expiry date selected.");
      setIsPreviousExpiryLoading(false);
      return undefined;
    }

    const controller = new AbortController();

    const loadPreviousExpiryData = async () => {
      setPreviousExpiryRows([]);
      setPreviousExpiryError("");
      setIsPreviousExpiryLoading(true);

      try {
        const response = await axios.get(STOCK_PREVIOUS_LTP_DATA_URL, {
          params: {
            stockSymbol: selectedSymbol,
            expiryDate: selectedExpiryDate,
            expirySymbol,
          },
          signal: controller.signal,
        });

        setPreviousExpiryRows(normalizePreviousLtpRows(response.data));
      } catch (error) {
        if (!axios.isCancel(error)) {
          console.error("Unable to load previous stock expiry LTP data", error);
          setPreviousExpiryRows([]);
          setPreviousExpiryError("Unable to load the selected expiry data.");
        }
      } finally {
        if (!controller.signal.aborted) {
          setIsPreviousExpiryLoading(false);
        }
      }
    };

    loadPreviousExpiryData();

    return () => controller.abort();
  }, [currentExpiryDate, selectedExpiryDate, selectedSymbol]);

  const isCurrentExpiry =
    !selectedExpiryDate || selectedExpiryDate === currentExpiryDate;
  const allOptionRows = useMemo(
    () => sortByStrike(isCurrentExpiry ? liveOptionRows : previousExpiryRows),
    [isCurrentExpiry, liveOptionRows, previousExpiryRows]
  );
  const firstOptionRow = allOptionRows?.[0] ?? liveFirstOptionRow;

  const dayRangeData = resolveDayRangeData(selectedMarketData);
  const liveData = resolveLiveData(selectedMarketData);
  const symbolName = formatStockSymbolName(resolveSymbolFromMarket(selectedMarketData, selectedSymbol));
  const currentSpotPrice = pickFirstFiniteNumber(
    liveData?.lp,
    liveData?.ltp,
    selectedMarketData?.lp,
    selectedMarketData?.ltp,
    selectedMarketData?.spotPrice,
    selectedMarketData?.stockSpotPrice,
    dayRangeData?.niftySpotPrice,
    dayRangeData?.spotPrice,
    dayRangeData?.lp,
    firstOptionRow?.stockSpotPrice,
    firstOptionRow?.spotPrice,
    firstOptionRow?.niftySpotPrice,
    firstOptionRow?.underlyingValue
  );
  const visibleOptionRows = useMemo(
    () => filterOptionRows(allOptionRows, currentSpotPrice, selectedStrikeFilter),
    [allOptionRows, currentSpotPrice, selectedStrikeFilter]
  );
  const ltpAnalysisRows = useMemo(
    () =>
      visibleOptionRows
        .map(buildLtpAnalysisRow)
        .filter(Boolean)
        .sort((firstRow, secondRow) => firstRow.strikePrice - secondRow.strikePrice),
    [visibleOptionRows]
  );

  usePageTitle(
    currentSpotPrice ?? "N/A",
    selectedExpiryDate || currentExpiryDate || "N/A"
  );

  const headerData = [
    {
      label1: "SPOT",
      value1: formatPrice(currentSpotPrice, 2),
    },
    {
      label1: "LOT",
      value1: firstOptionRow?.lotSize ?? liveFirstOptionRow?.lotSize ?? "--",
    },
    {
      label1: "ExpDate",
      value1: selectedExpiryDate || currentExpiryDate || "--",
    },
    {
      label1: "ExpMonth",
      value1:
        firstOptionRow?.currentMonthExpiry ?? firstOptionRow?.expirySymbol ?? "--",
    },
    {
      label1: "DateTime",
      value1: isCurrentExpiry
        ? firstOptionRow?.currentDateTime ?? "--"
        : firstOptionRow?.createdDateTime ??
          ([firstOptionRow?.createdDate, firstOptionRow?.createdTime]
            .filter(Boolean)
            .join(" ") || "--"),
    },
  ];

  return (
    <div className="stock-ltp-page">
      <div className="stock-ltp-shell">
        <header className="header">
          <nav className="nav-container">
            <div className="header-flex">
              <div className="left-nav">
                <div className="logo-container">
                  <img
                    src="/assets/images/noptionwave-logo-1.png"
                    className="header-logo"
                    alt="noptionwave logo"
                  />
                  <select
                    name="stockFilter"
                    className="stock-ltp-symbol-select"
                    value={selectedSymbol}
                    onChange={(event) => setSelectedSymbol(event.target.value)}
                    aria-label="Stock selector"
                  >
                    {symbolOptions.length > 0 ? (
                      symbolOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))
                    ) : (
                      <option value="">STOCK</option>
                    )}
                  </select>
                </div>

                <div className="header-item stock-ltp-symbol-item">
                  <span className="header-line">SYMBOL : {symbolName}</span>
                </div>

                {headerData.map((item, index) => (
                  <div className="header-item" key={`${item.label1}-${index}`}>
                    {(item.label1 != null || item.value1 != null) && (
                      <span className="header-line">
                        {item.label1}
                        {item.value1 != null && " : "}
                        {item.value1}
                      </span>
                    )}

                    {(item.label2 != null || item.value2 != null) && (
                      <span className="header-line">
                        {item.label2}
                        {item.value2 != null && " : "}
                        {item.value2}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </nav>
        </header>

        <section className="stock-ltp-layout">
          {ltpAnalysisRows.length > 0 ? (
            <div className="stock-ltp-table-wrap">
              <table className="responsive-table table-bordered text-center sticky-header stock-ltp-table">
                <thead>
                  <tr>
                    <th className="stock-ltp-call-head">ATH</th>
                    <th className="stock-ltp-call-head">ATL</th>
                    <th className="stock-ltp-call-head">ATH-ATL(%)</th>
                    <th className="stock-ltp-call-head">ATH-LTP(%)</th>
                    <th className="stock-ltp-call-head">LTP</th>
                    <th className="stock-ltp-index-head">
                      <div className="stock-ltp-header-filters">
                        <select
                          className="stock-ltp-range-select"
                          value={selectedStrikeFilter}
                          onChange={(event) => setSelectedStrikeFilter(event.target.value)}
                          aria-label="Visible strike range"
                        >
                          {RANGE_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                        <select
                          className="stock-ltp-expiry-select"
                          value={selectedExpiryDate}
                          onChange={(event) => setSelectedExpiryDate(event.target.value)}
                          disabled={stockExpiryDateList.length === 0}
                          aria-label="Stock expiry date selector"
                        >
                          {stockExpiryDateList.length === 0 ? (
                            <option value="">EXPIRY</option>
                          ) : (
                            stockExpiryDateList.map((expiryDate) => (
                              <option key={expiryDate} value={expiryDate}>
                                {expiryDate}
                              </option>
                            ))
                          )}
                        </select>
                      </div>
                    </th>
                    <th className="stock-ltp-put-head">LTP</th>
                    <th className="stock-ltp-put-head">ATH-LTP(%)</th>
                    <th className="stock-ltp-put-head">ATH-ATL(%)</th>
                    <th className="stock-ltp-put-head">ATL</th>
                    <th className="stock-ltp-put-head">ATH</th>
                  </tr>
                </thead>
                <tbody>
                  {ltpAnalysisRows.map((row, rowIndex) => {
                    const nextRow = ltpAnalysisRows[rowIndex + 1];
                    const isCallNearMaxLow = isPointDifferenceWithinLimit(row.callMaxLow, row.callLtp);
                    const isPutNearMaxLow = isPointDifferenceWithinLimit(row.putMaxLow, row.putLtp);
                    const callHighLowTierClass = getLtpRangeTierClass(row.callHighLowPercentage);
                    const callDownTierClass = getLtpRangeTierClass(row.callDownPercentage);
                    const putDownTierClass = getLtpRangeTierClass(row.putDownPercentage);
                    const putHighLowTierClass = getLtpRangeTierClass(row.putHighLowPercentage);
                    const isSpotBetween =
                      nextRow &&
                      currentSpotPrice != null &&
                      currentSpotPrice > Math.min(row.strikePrice, nextRow.strikePrice) &&
                      currentSpotPrice < Math.max(row.strikePrice, nextRow.strikePrice);

                    return (
                      <React.Fragment key={`stock-ltp-analysis-${row.strikePrice}-${rowIndex}`}>
                        <tr>
                          <td>{formatPrice(row.callMaxHigh, 2)}</td>
                          <td className={isCallNearMaxLow ? "stock-ltp-near-max-low" : ""}>
                            {formatPrice(row.callMaxLow, 2)}
                          </td>
                          <td className={callHighLowTierClass}>
                            {formatPercentValue(row.callHighLowPercentage, 2)}
                          </td>
                          <td className={callDownTierClass}>
                            {formatPercentValue(row.callDownPercentage, 2)}
                          </td>
                          <td className={isCallNearMaxLow ? "stock-ltp-near-max-low" : ""}>
                            {formatPrice(row.callLtp, 2)}
                          </td>
                          <td className="stock-ltp-strike-cell">{formatNumber(row.strikePrice, 0)}</td>
                          <td className={isPutNearMaxLow ? "stock-ltp-near-max-low" : ""}>
                            {formatPrice(row.putLtp, 2)}
                          </td>
                          <td className={putDownTierClass}>
                            {formatPercentValue(row.putDownPercentage, 2)}
                          </td>
                          <td className={putHighLowTierClass}>
                            {formatPercentValue(row.putHighLowPercentage, 2)}
                          </td>
                          <td className={isPutNearMaxLow ? "stock-ltp-near-max-low" : ""}>
                            {formatPrice(row.putMaxLow, 2)}
                          </td>
                          <td>{formatPrice(row.putMaxHigh, 2)}</td>
                        </tr>

                        {isSpotBetween ? (
                          <tr className="stock-ltp-spot-row">
                            <td colSpan="11">
                              SPOT - {formatPrice(currentSpotPrice, 2)}
                            </td>
                          </tr>
                        ) : null}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="stock-ltp-empty-state">
              {isPreviousExpiryLoading
                ? "Loading selected expiry data..."
                : previousExpiryError ||
                  (isCurrentExpiry
                    ? "Waiting for live stock option data..."
                    : "No data found for the selected expiry.")}
            </div>
          )}
        </section>

        <div className="fixed-footer">
          <span>noptionwave © 2026</span>
        </div>
      </div>
    </div>
  );
}
